'use client'

import { cn } from '@/lib/utils'
import * as React from 'react'
import { Checkbox } from './checkbox'

interface CheckboxGroupContextValue {
  value?: string[]
  defaultValue?: string[]
  onValueChange?: (value: string, checked: boolean) => void
  disabled?: boolean
  name?: string
}

const CheckboxGroupContext =
  React.createContext<CheckboxGroupContextValue | null>(null)

interface CheckboxGroupRootProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string[]
  defaultValue?: string[]
  onValueChange?: (value: string[]) => void
  disabled?: boolean
  name?: string
}

const CheckboxGroupRoot = React.forwardRef<
  HTMLDivElement,
  CheckboxGroupRootProps
>(
  (
    {
      className,
      value,
      defaultValue,
      onValueChange,
      disabled,
      name,
      children,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState<string[]>(
      defaultValue || []
    )
    const currentValue = value !== undefined ? value : internalValue

    const handleValueChange = (checkboxValue: string, checked: boolean) => {
      const newValue = checked
        ? [...currentValue, checkboxValue]
        : currentValue.filter((v) => v !== checkboxValue)

      setInternalValue(newValue)
      onValueChange?.(newValue)
    }

    return (
      <CheckboxGroupContext.Provider
        value={{
          value: currentValue,
          defaultValue,
          onValueChange: handleValueChange,
          disabled,
          name,
        }}
      >
        <div
          ref={ref}
          className={cn('grid gap-2', className)}
          role='group'
          {...props}
        >
          {children}
        </div>
      </CheckboxGroupContext.Provider>
    )
  }
)
CheckboxGroupRoot.displayName = 'CheckboxGroupRoot'

interface CheckboxGroupItemProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof Checkbox>,
    'value' | 'checked' | 'defaultChecked' | 'title'
  > {
  value: string
  title: React.ReactNode
}

const CheckboxGroupItem = React.forwardRef<
  React.ElementRef<typeof Checkbox>,
  CheckboxGroupItemProps
>(({ className, value, title, ...props }, ref) => {
  const context = React.useContext(CheckboxGroupContext)
  if (!context) {
    throw new Error('CheckboxGroup.Item must be used within CheckboxGroup.Root')
  }

  const { value: groupValue, onValueChange, disabled, name } = context
  const isChecked = groupValue?.includes(value)

  return (
    <div className='flex items-center space-x-2'>
      <Checkbox
        ref={ref}
        id={`checkbox-${value}`}
        checked={isChecked}
        disabled={disabled}
        name={name}
        onCheckedChange={(checked) => {
          onValueChange?.(value, checked === true)
        }}
        title={title}
        className={className}
        {...props}
      />
    </div>
  )
})
CheckboxGroupItem.displayName = 'CheckboxGroupItem'

export { CheckboxGroupItem, CheckboxGroupRoot }
